import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function AllocationProfileViewDialog({ open, onClose, profile }) {
  if (!profile) return null;

  const calculateQuarterTotal = (quarter) => {
    const startMonth = (quarter - 1) * 3 + 1;
    const endMonth = quarter * 3;
    const items = profile.items || [];
    return items
      .filter(
        (item) => item.month_num >= startMonth && item.month_num <= endMonth
      )
      .reduce((sum, item) => sum + parseFloat(item.percent_of_year || 0), 0);
  };

  const calculateTotal = () => {
    const items = profile.items || [];
    return items.reduce(
      (sum, item) => sum + parseFloat(item.percent_of_year || 0),
      0
    );
  };

  const renderQuarterRow = (quarter) => {
    const quarterTotal = calculateQuarterTotal(quarter);
    const quarterPercent = (quarterTotal * 100).toFixed(2);
    const quarterName = `Q${quarter} Total`;

    return (
      <TableRow key={`quarter-${quarter}`} sx={{ backgroundColor: "#f5f5f5" }}>
        <TableCell sx={{ fontWeight: "bold", pl: 2 }}>{quarterName}</TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {quarterTotal.toFixed(5)}
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: "bold" }}>
          {quarterPercent}%
        </TableCell>
      </TableRow>
    );
  };

  const items = profile.items || [];
  const sortedItems = [...items].sort((a, b) => a.month_num - b.month_num);
  const total = calculateTotal();
  const totalPercent = (total * 100).toFixed(2);
  const isValidTotal = Math.abs(total - 1.0) <= 0.001;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="h6">
            Allocation Profile: {profile.profile_name}
          </Typography>
          <Chip
            label={profile.is_active ? "Active" : "Inactive"}
            color={profile.is_active ? "success" : "error"}
            size="small"
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Profile ID: {profile.profile_ndx}
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Monthly Allocation Breakdown
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "#e0e0e0" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>Month</TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Percent of Year
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    Percentage (%)
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedItems.map((item) => (
                  <React.Fragment key={item.month_num}>
                    <TableRow>
                      <TableCell>{MONTH_NAMES[item.month_num - 1]}</TableCell>
                      <TableCell align="right">
                        {parseFloat(item.percent_of_year).toFixed(5)}
                      </TableCell>
                      <TableCell align="right">
                        {(parseFloat(item.percent_of_year) * 100).toFixed(2)}%
                      </TableCell>
                    </TableRow>
                    {/* Add quarter total rows after March, June, September, and December */}
                    {item.month_num === 3 && renderQuarterRow(1)}
                    {item.month_num === 6 && renderQuarterRow(2)}
                    {item.month_num === 9 && renderQuarterRow(3)}
                    {item.month_num === 12 && renderQuarterRow(4)}
                  </React.Fragment>
                ))}
                <TableRow sx={{ backgroundColor: "#e8f5e9" }}>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Annual Total
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: "bold" }}>
                    {total.toFixed(5)}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      fontWeight: "bold",
                      color: isValidTotal ? "success.main" : "error.main",
                    }}
                  >
                    {totalPercent}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {!isValidTotal && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`Warning: Total is ${totalPercent}%, should be 100%`}
                color="warning"
                variant="outlined"
              />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AllocationProfileViewDialog;
